import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import { useAnalyticConfigs, useAnalyticConfigKeys } from 'api/hooks/use-analytic-configs'
import MccCodePicker from './MccCodePicker'
import KeywordPicker from './KeywordPicker'
import HolidayDatePicker from './HolidayDatePicker'
import ListConfigInput from './ListConfigInput'
import ListRowsConfigInput from './ListRowsConfigInput'

const NUMERIC_DATATYPES = ['number', 'integer', 'percent']
// List settings edited one entry per row: entries that wrap or contain commas
// (addresses) can't be told apart in a text box.
const ROW_LIST_SETTINGS = {
  remit_shared_address_exceptions: {
    itemLabel: 'address',
    hint:
      'One address per row. Add city and state to limit it to that city, e.g. "100 Main St, ' +
      "Springfield IL\". Case, punctuation and abbreviations (Avenue/Ave, Third/3rd) don't matter."
  }
}

// The config_keys endpoint serves each key's definition: from the analytic's
// recipe for custom analytics, from the config_keys table for core ones.
const inputType = (datatype) => {
  if (!datatype || NUMERIC_DATATYPES.includes(datatype)) return 'number'
  return datatype === 'array' ? 'array' : 'string'
}

const ConfigEditor = ({ analyticId, onConfigChange, onHasChanges, disabled }) => {
  const { data: configData, isLoading: configsLoading } = useAnalyticConfigs()
  const { data: configKeysData, isLoading: keysLoading } = useAnalyticConfigKeys({ analyticId })
  const [localValues, setLocalValues] = useState({})
  const prevConfigDataRef = useRef(null)

  const allConfigs = configData?.values || configData || {}
  const dbDescriptions = configData?.descriptions || {}
  const dbDefaultValues = configData?.default_values || {}

  const configKeys = configKeysData?.config_keys || []
  const apiMetadata = configKeysData?.config_metadata || {}

  const metadataFor = (key) => {
    const apiMeta = apiMetadata[key]
    if (!apiMeta) return null
    return {
      label: apiMeta.label || key,
      type: inputType(apiMeta.datatype),
      default: apiMeta.default,
      description: apiMeta.description
    }
  }

  useEffect(() => {
    // Re-initialize when either configData OR the keys/metadata change — for
    // custom analytics, defaults live in API metadata, which loads
    // independently of the analytic_configs query. The signature includes the
    // actual key names (not just the count) so a recipe edit that swaps one
    // tunable for another of the same arity still resets localValues.
    const signature = `${configData ? 'cd' : ''}-${configKeysData ? 'ck' : ''}-${configKeys.join('|')}`
    if (configData && configKeys.length > 0 && signature !== prevConfigDataRef.current) {
      prevConfigDataRef.current = signature
      const values = configData?.values || configData || {}
      const initial = {}
      configKeys.forEach((key) => {
        initial[key] = values[key] !== undefined ? values[key] : metadataFor(key)?.default
      })
      setLocalValues(initial)
      if (onHasChanges) {
        onHasChanges(false)
      }
    }
  }, [configData, configKeysData, configKeys, onHasChanges])

  const handleChange = (key, value) => {
    const metadata = metadataFor(key)
    let parsedValue = value

    if (metadata?.type === 'number') {
      parsedValue = value === '' ? '' : parseFloat(value)
    }

    const newValues = { ...localValues, [key]: parsedValue }
    setLocalValues(newValues)
    if (onConfigChange) {
      // A cleared field means "no override", not a blank threshold.
      onConfigChange(Object.fromEntries(Object.entries(newValues).filter(([, v]) => v !== '')))
    }
    if (onHasChanges) {
      onHasChanges(true)
    }
  }


  if (configsLoading || keysLoading) {
    return (
      <div className="mb-3">
        <p className="text-muted">Loading configuration...</p>
      </div>
    )
  }

  if (configKeys.length === 0) {
    return (
      <div className="mb-3">
        <p className="text-muted mb-0">
          This analytic does not have configurable parameters.
        </p>
      </div>
    )
  }

  return (
    <div className="mb-3">
      {configKeys.map((key) => {
        const metadata = metadataFor(key)
        const value = localValues[key]

        if (!metadata) return null

        if (metadata?.type === 'string') {
          return (
            <Row key={key} className="mb-3 align-items-center">
              <Col sm={8}>
                <Form.Label className="small fw-bold mb-0">{metadata.label}</Form.Label>
                {(dbDescriptions[key] || metadata.description) && (
                  <Form.Text className="text-muted d-block">{dbDescriptions[key] || metadata.description}</Form.Text>
                )}
              </Col>
              <Col sm={4}>
                <Form.Control
                  type="text"
                  value={value !== undefined ? value : ''}
                  onChange={(e) => handleChange(key, e.target.value)}
                  disabled={disabled}
                />
                <Form.Text className="text-muted">Default: {dbDefaultValues[key] ?? metadata.default}</Form.Text>
              </Col>
            </Row>
          )
        }

        if (key.endsWith('_keywords')) {
          return (
            <Row key={key} className="mb-3 align-items-center">
              <Col sm={8}>
                <Form.Label className="small fw-bold mb-0">{metadata.label}</Form.Label>
                {(dbDescriptions[key] || metadata.description) && (
                  <Form.Text className="text-muted d-block">{dbDescriptions[key] || metadata.description}</Form.Text>
                )}
              </Col>
              <Col sm={4}>
                <KeywordPicker
                  label={metadata.label}
                  selectedKeywords={Array.isArray(value) ? value : []}
                  defaultKeywords={dbDefaultValues[key] || metadata.default || []}
                  onChange={(keywords) => handleChange(key, keywords)}
                  disabled={disabled}
                />
              </Col>
            </Row>
          )
        }

        if (key === 'restricted_mcc_codes') {
          return (
            <Row key={key} className="mb-3 align-items-center">
              <Col sm={8}>
                <Form.Label className="small fw-bold mb-0">{metadata.label}</Form.Label>
                {(dbDescriptions[key] || metadata.description) && (
                  <Form.Text className="text-muted d-block">{dbDescriptions[key] || metadata.description}</Form.Text>
                )}
              </Col>
              <Col sm={4}>
                <MccCodePicker
                  selectedCodes={Array.isArray(value) ? value : []}
                  onChange={(codes) => handleChange(key, codes)}
                  disabled={disabled}
                />
              </Col>
            </Row>
          )
        }

        if (key === 'included_holidays') {
          return (
            <Row key={key} className="mb-3 align-items-center">
              <Col sm={8}>
                <Form.Label className="small fw-bold mb-0">{metadata.label}</Form.Label>
                {(dbDescriptions[key] || metadata.description) && (
                  <Form.Text className="text-muted d-block">{dbDescriptions[key] || metadata.description}</Form.Text>
                )}
              </Col>
              <Col sm={4}>
                <HolidayDatePicker
                  label={metadata.label}
                  selectedDates={Array.isArray(value) ? value : []}
                  defaultDates={metadata.default || []}
                  onChange={(dates) => handleChange(key, dates)}
                  disabled={disabled}
                />
              </Col>
            </Row>
          )
        }

        if (metadata?.type === 'array') {
          return (
            <Row key={key} className="mb-3 align-items-center">
              <Col sm={8}>
                <Form.Label className="small fw-bold mb-0">{metadata.label}</Form.Label>
                {(dbDescriptions[key] || metadata.description) && (
                  <Form.Text className="text-muted d-block">{dbDescriptions[key] || metadata.description}</Form.Text>
                )}
              </Col>
              <Col sm={4}>
                {ROW_LIST_SETTINGS[key] ? (
                  <ListRowsConfigInput
                    value={Array.isArray(value) ? value : []}
                    onChange={(list) => handleChange(key, list)}
                    itemLabel={ROW_LIST_SETTINGS[key].itemLabel}
                    hint={ROW_LIST_SETTINGS[key].hint}
                    disabled={disabled}
                  />
                ) : (
                  <ListConfigInput
                    value={Array.isArray(value) ? value : []}
                    onChange={(list) => handleChange(key, list)}
                    disabled={disabled}
                  />
                )}
                <Form.Text className="text-muted">Default: {JSON.stringify(dbDefaultValues[key] ?? metadata.default)}</Form.Text>
              </Col>
            </Row>
          )
        }

        return (
          <Row key={key} className="mb-3 align-items-center">
            <Col sm={8}>
              <Form.Label className="small fw-bold mb-0">{metadata.label}</Form.Label>
              {(dbDescriptions[key] || metadata.description) && (
                <Form.Text className="text-muted d-block">{dbDescriptions[key] || metadata.description}</Form.Text>
              )}
            </Col>
            <Col sm={4}>
              <Form.Control
                type="number"
                step={(dbDefaultValues[key] ?? metadata.default) < 1 ? '0.01' : '1'}
                value={value !== undefined ? value : ''}
                onChange={(e) => handleChange(key, e.target.value)}
                disabled={disabled}
              />
              <Form.Text className="text-muted">Default: {dbDefaultValues[key] ?? metadata.default}</Form.Text>
            </Col>
          </Row>
        )
      })}
    </div>
  )
}

ConfigEditor.propTypes = {
  analyticId: PropTypes.string.isRequired,
  onConfigChange: PropTypes.func,
  onHasChanges: PropTypes.func,
  disabled: PropTypes.bool
}

export default ConfigEditor
